import { AD_LAYOUT_STUBS, AD_PLACEMENTS, ARTICLE_TYPES } from "@consts";
import { canonical } from "@utils";
import { mapGetters } from "vuex";
import { getArticles } from "~/common/services/articleService";
import NewsletterBanner from "~/components/NewsletterBanner/NewsletterBanner.vue";

const performFetch = async (axios, error, pageNumber, isWordleArticlesPage) => {
	const getParamsForRequest = (currentPage, articlesOnTop) => {
		let limit = 15;

		const start =
			currentPage === 1 ? 0 : (currentPage - 1) * limit + articlesOnTop;
		limit = currentPage === 1 ? limit + articlesOnTop : limit;

		const params = { start, limit, orderBy: "-publishedAt" };

		if (articlesOnTop !== 0) {
			params.articlesOnTop = articlesOnTop;
		}
		return params;
	};

	const getArticleData = (articlesOnTop, response) => {
		const hasResponse = response.errors === null;
		const total = response.meta?.total;
		const totalPages = Math.ceil((total - articlesOnTop) / 15);
		const articles = hasResponse ? response.data : [];

		return { totalPages, articles };
	};

	const getResponseOrFail = async (
		axios,
		params,
		error,
		isWordleArticlesPage
	) => {
		if (params.start < 0 || isNaN(params.start)) {
			error({ statusCode: 404 });
		}

		const wordleRequestPath = isWordleArticlesPage
			? "https://api.yourdictionary.com/cms/v1/wfa/category/.wordle.word-lists/articles"
			: undefined;
		const response = await getArticles(axios, params, wordleRequestPath);

		if (response.status === 404) {
			error({ statusCode: 404 });
		}
		return response;
	};

	const ARTICLES_ON_TOP = isWordleArticlesPage ? 0 : 4;
	const response = await getResponseOrFail(
		axios,
		getParamsForRequest(pageNumber, ARTICLES_ON_TOP),
		error,
		isWordleArticlesPage
	);

	return getArticleData(ARTICLES_ON_TOP, response);
};

export default {
	name: "BlogIndex",
	components: { NewsletterBanner },
	watch: {
		"$route.query": "$fetch",
	},
	head() {
		try {
			let title = this.$t(this.$route.meta.title);
			let description = this.$t(this.$route.meta.description);
			if (this.currentPage > 1) {
				if (this.isWordleArticlesIndexPage) {
					title = `Page ${this.currentPage} - ${title}`;
					description = `Page ${this.currentPage} - ${description}`;
				} else {
					title = `Page ${this.currentPage} - ${this.$t(
						"HeaderArticlesIndexMetaTitleSubsequentPages"
					)}`;
					description = `Page ${this.currentPage} - ${this.$t(
						"HeaderArticlesIndexMetaDescriptionSubsequentPages"
					)}`;
				}
			}

			return {
				title,
				meta: [
					{
						hid: "description",
						name: "description",
						content: description,
					},
					{
						hid: "max-image-preview",
						name: "robots",
						content: "max-image-preview:large",
					},
				],
				link: [canonical(this.$route)],
				htmlAttrs: {
					lang: "en",
				},
			};
		} catch (error) {
			console.error(error);
		}
	},
	// On the server side, the error function will actually show the error layout
	// if a 404 happens on the api side...
	async asyncData({ $axios, error, params, store }) {
		if (process.client) {
			return;
		}

		const pageNumber = params.page ? parseInt(params.page) : 1;

		return await performFetch(
			$axios,
			error,
			pageNumber,
			store.getters["navigation/isWordleArticlesIndexPage"]
		);
	},
	// ...and on the client side, we will need to use the fetch in order to show loadings,
	// but handling the error here will not show the error layout when the api returns a 404,
	// for some reason...
	async fetch() {
		if (process.server) {
			return;
		}

		const { totalPages, articles } = await performFetch(
			this.$axios,
			this.$nuxt.error,
			this.currentPage,
			this.isWordleArticlesIndexPage
		);

		this.totalPages = totalPages;
		this.articles = articles;
	},
	data() {
		return {
			articles: [],
			ARTICLE_TYPES,
			AD_LAYOUT_STUBS,
			AD_PLACEMENTS,
			totalPages: 1,
		};
	},
	computed: {
		...mapGetters("navigation", ["isWordleArticlesIndexPage"]),
		isFirstPage() {
			return this.currentPage === 1;
		},
		leadArticle() {
			if (this.articles.length >= 1) {
				return this.articles[0];
			}

			return null;
		},
		latestArticles() {
			return this.articles.length ? this.articles.slice(1, 4) : null;
		},
		remainingArticles() {
			if (this.currentPage === 1 && !this.isWordleArticlesIndexPage) {
				return this.articles.length ? this.articles.slice(4) : null;
			}

			return this.articles;
		},
		remainingArticlesGrouped() {
			const output = [];
			const groupLength = Math.ceil(this.remainingArticles.length / 3);
			for (let i = 0; i < groupLength; i++) {
				const articles = this.remainingArticles.slice(
					i * 3,
					(i + 1) * 3
				);
				let ad = null;
				const maxAds = 5;
				if (
					this.isSmallOrMediumPortraitDevice &&
					articles.length === 3 &&
					i < maxAds
				) {
					ad = {
						stub: AD_LAYOUT_STUBS.MOB_IN_CONTENT,
						placement: AD_PLACEMENTS[`MOB_IN_CONTENT_${i + 2}`],
					};
				}

				output.push({
					articles,
					ad,
				});
			}

			return output;
		},
		currentPage() {
			return this.$route.params.page
				? parseInt(this.$route.params.page)
				: 1;
		},

		h1Text() {
			return this.isWordleArticlesIndexPage
				? "Wordle Word Lists"
				: this.$t("ArticlesIndexPageH1");
		},
		showLeadArticles() {
			return (
				this.leadArticle &&
				this.isFirstPage &&
				!this.isWordleArticlesIndexPage
			);
		},
		paginatedPagePath() {
			return this.isWordleArticlesIndexPage
				? "/wordle/word-lists/"
				: "/blog/";
		},
	},
};
